<template>
    <modal name="modalProgramGroupContribution" :width="'76%'" :height="'auto'" :adaptive="true" :scrollable="true"
        :click-to-close="false">
        <form class="contribution-form" @submit.prevent="saveContribution">

            <!-- Encabezado -->
            <div class="form-header">
                <div class="form-header__content">
                    <div class="form-header__icon">
                        <i class="fa-solid fa-hand-holding-dollar"></i>
                    </div>

                    <div>
                        <h5 class="form-header__title">
                            Registrar aportación
                        </h5>

                        <p class="form-header__subtitle">
                            Captura el pago y distribúyelo entre las aportaciones pendientes.
                        </p>
                    </div>
                </div>

                <button type="button" class="form-close" title="Cerrar" @click="closeModal">
                    <i class="fa-solid fa-xmark"></i>
                </button>
            </div>

            <!-- Contenido -->
            <div class="form-body">

                <div class="contribution-layout">

                    <!-- Columna principal -->
                    <div class="contribution-main">

                        <!-- Origen del pago -->
                        <section class="form-section">
                            <div class="section-header">
                                <div class="section-number">1</div>

                                <div>
                                    <h6 class="section-title">
                                        Origen del pago
                                    </h6>

                                    <p class="section-description">
                                        Indica de dónde se tomará el dinero.
                                    </p>
                                </div>
                            </div>

                            <div class="source-options">

                                <label class="source-option" :class="{
                                    'source-option--active': form.source_key === 'direct'
                                }">
                                    <input v-model="form.source_key" type="radio" value="direct"
                                        @change="clearError('source_key')">

                                    <span class="source-option__icon">
                                        <i class="fa-solid fa-building-columns"></i>
                                    </span>

                                    <span class="source-option__content">
                                        <strong>Aportación directa</strong>
                                        <small>
                                            El pago proviene de una transferencia o efectivo.
                                        </small>
                                    </span>

                                    <span class="source-option__check">
                                        <i class="fa-solid fa-circle-check"></i>
                                    </span>
                                </label>

                                <label class="source-option" :class="{
                                    'source-option--active': form.source_key === 'wallet'
                                }">
                                    <input v-model="form.source_key" type="radio" value="wallet"
                                        @change="clearError('source_key')">

                                    <span class="source-option__icon">
                                        <i class="fa-solid fa-wallet"></i>
                                    </span>

                                    <span class="source-option__content">
                                        <strong>Desde monedero</strong>
                                        <small>
                                            El importe se descontará del saldo disponible.
                                        </small>
                                    </span>

                                    <span class="source-option__check">
                                        <i class="fa-solid fa-circle-check"></i>
                                    </span>
                                </label>

                            </div>

                            <div v-if="errors.source_key" class="field-error mt-2">
                                <i class="fa-solid fa-circle-exclamation"></i>
                                {{ errors.source_key[0] }}
                            </div>

                            <!-- Cuenta de monedero -->
                            <div v-if="form.source_key === 'wallet'" class="wallet-panel">
                                <div class="form-group mb-0">
                                    <label class="field-label">
                                        Cuenta origen del monedero
                                        <span class="required-mark">*</span>
                                    </label>

                                    <select v-model="form.bank_id" class="form-control"
                                        :class="{ 'is-invalid': errors.bank_id }" @change="loadWallet">
                                        <option value="">
                                            Seleccione una cuenta
                                        </option>

                                        <option v-for="account in catalogue.bank_accounts_companies"
                                            :key="account.bank_account_id" :value="account.bank_account_id">
                                            {{ getAccountLabel(account) }}
                                        </option>
                                    </select>

                                    <div v-if="errors.bank_id" class="field-error">
                                        {{ errors.bank_id[0] }}
                                    </div>
                                </div>

                                <div v-if="form.bank_id" class="wallet-balance" :class="{
                                    'wallet-balance--danger': walletInsufficient
                                }">
                                    <div class="wallet-balance__icon">
                                        <i class="fa-solid fa-wallet"></i>
                                    </div>

                                    <div>
                                        <small>Saldo disponible</small>

                                        <strong>
                                            {{ Number(wallet_balance || 0) | toCurrency }}
                                        </strong>
                                    </div>
                                </div>

                                <div v-if="walletInsufficient" class="validation-message validation-message--danger">
                                    <i class="fa-solid fa-triangle-exclamation"></i>

                                    El monto capturado supera el saldo disponible
                                    en el monedero.
                                </div>
                            </div>
                        </section>

                        <!-- Datos del movimiento -->
                        <section class="form-section">
                            <div class="section-header">
                                <div class="section-number">2</div>

                                <div>
                                    <h6 class="section-title">
                                        Datos del movimiento
                                    </h6>

                                    <p class="section-description">
                                        Selecciona la cuenta destino, el tipo de pago y la fecha.
                                    </p>
                                </div>
                            </div>

                            <div class="form-grid">

                                <div class="form-group">
                                    <label class="field-label">
                                        Cuenta bancaria destino
                                        <span class="required-mark">*</span>
                                    </label>

                                    <select v-model="form.bank_account_company_id" class="form-control" :class="{
                                        'is-invalid': errors.bank_account_company_id
                                    }" @change="clearError('bank_account_company_id')">
                                        <option value="">
                                            Seleccione una cuenta
                                        </option>

                                        <option v-for="account in catalogue.bank_accounts_groups" :key="account.id"
                                            :value="account.id">
                                            {{ account.label }}
                                        </option>
                                    </select>

                                    <div v-if="errors.bank_account_company_id" class="field-error">
                                        {{ errors.bank_account_company_id[0] }}
                                    </div>
                                </div>

                                <div class="form-group">
                                    <label class="field-label">
                                        Tipo de movimiento
                                        <span class="required-mark">*</span>
                                    </label>

                                    <select v-model="form.type_payments_id" class="form-control" :class="{
                                        'is-invalid': errors.type_payments_id
                                    }" @change="clearError('type_payments_id')">
                                        <option :value="null">
                                            Seleccione un tipo
                                        </option>

                                        <option value="2">
                                            Transferencia
                                        </option>

                                        <option value="5">
                                            Efectivo
                                        </option>
                                    </select>

                                    <div v-if="errors.type_payments_id" class="field-error">
                                        {{ errors.type_payments_id[0] }}
                                    </div>
                                </div>

                                <div class="form-group">
                                    <label class="field-label">
                                        Fecha del movimiento
                                        <span class="required-mark">*</span>
                                    </label>

                                    <date-picker v-model="form.date" class="date-picker-field" format="YYYY-MM-DD"
                                        valueType="format" :clearable="false" @change="clearError('date')" />

                                    <div v-if="errors.date" class="field-error">
                                        {{ errors.date[0] }}
                                    </div>
                                </div>

                                <div class="form-group">
                                    <label class="field-label">
                                        Monto total pagado
                                        <span class="required-mark">*</span>
                                    </label>

                                    <div class="amount-field">
                                        <span class="amount-field__symbol">
                                            $
                                        </span>

                                        <money v-model="form.amount" class="form-control amount-field__input"
                                            :class="{ 'is-invalid': errors.amount }" @input="clearError('amount')" />
                                    </div>

                                    <small class="field-help">
                                        Captura el monto total recibido.
                                    </small>

                                    <div v-if="errors.amount" class="field-error">
                                        {{ errors.amount[0] }}
                                    </div>
                                </div>

                            </div>
                        </section>

                        <!-- Distribución -->
                        <section class="form-section form-section--distribution">
                            <div class="section-header section-header--actions">
                                <div class="section-header__left">
                                    <div class="section-number">3</div>

                                    <div>
                                        <h6 class="section-title">
                                            Distribución del pago
                                        </h6>

                                        <p class="section-description">
                                            Aplica el monto a una o varias aportaciones pendientes.
                                        </p>
                                    </div>
                                </div>

                            </div>

                            <!-- Cargando -->
                            <div v-if="loadingSlots" class="loading-state">
                                <i class="fa-solid fa-circle-notch fa-spin"></i>

                                <span>
                                    Consultando aportaciones pendientes...
                                </span>
                            </div>

                            <!-- Tabla -->
                            <div v-else-if="contributionSlots.length" class="distribution-table-wrapper">
                                <table class="distribution-table">
                                    <thead>
                                        <tr>
                                            <th>Aportación</th>
                                            <th class="text-right">Esperado</th>
                                            <th class="text-right">Pagado</th>
                                            <th class="text-right">Pendiente</th>
                                            <th class="apply-column">
                                                Monto a aplicar
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        <tr v-for="slot in contributionSlots" :key="slot.id">
                                            <td>
                                                <div class="slot-information">
                                                    <div class="slot-icon">
                                                        <i class="fa-solid fa-file-invoice-dollar"></i>
                                                    </div>

                                                    <div>
                                                        <strong>
                                                            {{ slot.label }}
                                                        </strong>

                                                        <small v-if="slot.due_date">
                                                            Vencimiento:
                                                            {{ slot.due_date }}
                                                        </small>
                                                    </div>
                                                </div>

                                                <div class="slot-progress">
                                                    <div class="slot-progress__bar" :style="{
                                                        width: getSlotProgress(slot) + '%'
                                                    }"></div>
                                                </div>
                                            </td>

                                            <td class="text-right amount-cell">
                                                {{
                                                    Number(
                                                        slot.expected_amount ||
                                                        slot.amount ||
                                                        0
                                                    ) | toCurrency
                                                }}
                                            </td>

                                            <td class="text-right paid-cell">
                                                {{
                                                    Number(slot.paid_amount || 0)
                                                    | toCurrency
                                                }}
                                            </td>

                                            <td class="text-right pending-cell">
                                                {{
                                                    Number(slot.pending_amount || 0)
                                                    | toCurrency
                                                }}
                                            </td>

                                            <td class="apply-column">
                                                <div class="apply-field">
                                                    <money v-model="slot.apply_amount"
                                                        class="form-control form-control-sm"
                                                        @input="normalizeApplyAmount(slot)" />

                                                    <button type="button" class="apply-full-button"
                                                        title="Aplicar todo lo pendiente"
                                                        @click="applyFullPending(slot)">
                                                        Todo
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <!-- Sin aportaciones -->
                            <div v-else class="empty-state">
                                <div class="empty-state__icon">
                                    <i class="fa-solid fa-circle-check"></i>
                                </div>

                                <div>
                                    <strong>
                                        No hay aportaciones pendientes
                                    </strong>

                                    <p>
                                        Todas las aportaciones disponibles ya fueron cubiertas.
                                    </p>
                                </div>
                            </div>

                            <!-- Mensajes de distribución -->
                            <div class="distribution-validations">

                                <div v-if="Number(totalApplyAmount) > Number(form.amount || 0)"
                                    class="validation-message validation-message--danger">
                                    <i class="fa-solid fa-circle-exclamation"></i>

                                    El total aplicado no puede ser mayor al monto pagado.
                                </div>

                                <div v-if="hasUnappliedAmount" class="validation-message validation-message--warning">
                                    <i class="fa-solid fa-triangle-exclamation"></i>

                                    Faltan
                                    <strong>
                                        {{ remainingToApply | toCurrency }}
                                    </strong>
                                    por distribuir.
                                </div>

                                <div v-if="Number(totalApplyAmount) <= 0 && contributionSlots.length"
                                    class="validation-message validation-message--warning">
                                    <i class="fa-solid fa-circle-info"></i>

                                    Captura al menos un monto para continuar.
                                </div>

                            </div>
                        </section>

                        <!-- Excedente -->
                        <section v-if="isAllPendingCovered && Number(excessAmount) > 0"
                            class="form-section excess-section">
                            <div class="section-header">
                                <div class="section-number section-number--success">
                                    <i class="fa-solid fa-plus"></i>
                                </div>

                                <div>
                                    <h6 class="section-title">
                                        Excedente detectado
                                    </h6>

                                    <p class="section-description">
                                        El monto restante será enviado al monedero seleccionado.
                                    </p>
                                </div>
                            </div>

                            <div class="excess-summary">
                                <span>Excedente</span>

                                <strong>
                                    {{ Number(excessAmount || 0) | toCurrency }}
                                </strong>
                            </div>

                            <div class="form-group mb-0">
                                <label class="field-label">
                                    Cuenta destino del excedente
                                    <span class="required-mark">*</span>
                                </label>

                                <select v-model="form.excess_bank_id" class="form-control"
                                    :class="{ 'is-invalid': errors.excess_bank_id }"
                                    @change="clearError('excess_bank_id')">
                                    <option value="">
                                        Seleccione una cuenta
                                    </option>

                                    <option v-for="account in catalogue.bank_accounts_companies"
                                        :key="account.bank_account_id" :value="account.bank_account_id">
                                        {{ getAccountLabel(account) }}
                                    </option>
                                </select>

                                <div v-if="errors.excess_bank_id" class="field-error">
                                    {{ errors.excess_bank_id[0] }}
                                </div>
                            </div>
                        </section>

                    </div>

                    <!-- Resumen lateral -->
                    <aside class="contribution-summary">
                        <div class="summary-card">
                            <div class="summary-card__header">
                                <div>
                                    <small>Resumen del movimiento</small>
                                    <strong>Totales</strong>
                                </div>

                                <i class="fa-solid fa-calculator"></i>
                            </div>

                            <div class="summary-row">
                                <span>Monto pagado</span>

                                <strong>
                                    {{ Number(form.amount || 0) | toCurrency }}
                                </strong>
                            </div>

                            <div class="summary-row">
                                <span>Total pendiente</span>

                                <strong>
                                    {{ Number(totalPendingAmount || 0) | toCurrency }}
                                </strong>
                            </div>

                            <div class="summary-row summary-row--primary">
                                <span>Total aplicado</span>

                                <strong>
                                    {{ Number(totalApplyAmount || 0) | toCurrency }}
                                </strong>
                            </div>

                            <div v-if="Number(excessAmount) > 0" class="summary-row summary-row--success">
                                <span>Excedente</span>

                                <strong>
                                    {{ Number(excessAmount || 0) | toCurrency }}
                                </strong>
                            </div>

                            <div v-else-if="Number(remainingToApply) > 0" class="summary-row summary-row--warning">
                                <span>Falta distribuir</span>

                                <strong>
                                    {{ Number(remainingToApply || 0) | toCurrency }}
                                </strong>
                            </div>

                            <div class="summary-progress">
                                <div class="summary-progress__header">
                                    <span>Distribución</span>
                                    <strong>{{ distributionPercentage }}%</strong>
                                </div>

                                <div class="summary-progress__track">
                                    <div class="summary-progress__value" :style="{
                                        width: distributionPercentage + '%'
                                    }"></div>
                                </div>
                            </div>

                            <div v-if="form.source_key === 'wallet'" class="summary-wallet" :class="{
                                'summary-wallet--danger': walletInsufficient
                            }">
                                <span>Saldo monedero</span>

                                <strong>
                                    {{ Number(wallet_balance || 0) | toCurrency }}
                                </strong>
                            </div>
                        </div>

                        <div class="summary-help">
                            <i class="fa-solid fa-circle-info"></i>

                            <p>
                                El monto aplicado se distribuye únicamente entre
                                las aportaciones seleccionadas.
                            </p>
                        </div>
                    </aside>

                </div>
            </div>

            <!-- Pie -->
            <div class="form-footer">
                <button type="button" class="btn btn-light footer-button" :disabled="saving" @click="closeModal">
                    Cancelar
                </button>

                <button type="submit" class="btn btn-success footer-button footer-button--save"
                    :disabled="isSaveDisabled || saving">
                    <i v-if="saving" class="fa-solid fa-circle-notch fa-spin"></i>

                    <i v-else class="fa-solid fa-floppy-disk"></i>

                    {{ saving ? 'Guardando...' : 'Guardar aportación' }}
                </button>
            </div>

        </form>
    </modal>
</template>

<script>
import request from "@/services/request";
import { mapActions } from "vuex";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import "vue2-datepicker/locale/es";
import Catalogue from "@/services/catalogue";

export default {
    components: {
        DatePicker
    },

    data() {
        return {
            catalogue: new Catalogue(),
            errors: {},
            wallet_balance: 0,
            contributionSlots: [],
            form: this.getDefaultForm(),
            contribution_count: 0,
            extra_contribution_count: 0,
            loadingSlots: false,
            saving: false
        };
    },

    computed: {
        totalPendingAmount() {
            return this.contributionSlots.reduce((sum, slot) => {
                return sum + Number(slot.pending_amount || 0);
            }, 0);
        },

        totalApplyAmount() {
            return this.contributionSlots.reduce((sum, slot) => {
                return sum + Number(slot.apply_amount || 0);
            }, 0);
        },

        isAllPendingCovered() {
            return (
                this.roundMoney(this.totalApplyAmount) >=
                this.roundMoney(this.totalPendingAmount)
            );
        },

        remainingToApply() {
            const amount = Number(this.form.amount || 0);
            const applied = Number(this.totalApplyAmount || 0);

            return Math.max(
                this.roundMoney(amount - applied),
                0
            );
        },

        hasUnappliedAmount() {
            const amount = this.roundMoney(this.form.amount);
            const applied = this.roundMoney(this.totalApplyAmount);
            const pending = this.roundMoney(this.totalPendingAmount);

            if (amount <= 0 || applied >= pending) {
                return false;
            }

            return amount > applied;
        },

        distributionPercentage() {
            const amount = Number(this.form.amount || 0);
            const applied = Number(this.totalApplyAmount || 0);

            if (amount <= 0) {
                return 0;
            }

            return Math.min(
                Math.round((applied / amount) * 100),
                100
            );
        },

        walletInsufficient() {
            return (
                this.form.source_key === "wallet" &&
                Number(this.form.amount || 0) >
                Number(this.wallet_balance || 0)
            );
        },

        isSaveDisabled() {
            const amount = this.roundMoney(this.form.amount);
            const applied = this.roundMoney(this.totalApplyAmount);
            const pending = this.roundMoney(this.totalPendingAmount);

            if (!this.form.source_key) {
                return true;
            }

            if (!this.form.bank_account_company_id) {
                return true;
            }

            if (!this.form.type_payments_id) {
                return true;
            }

            if (!this.form.date) {
                return true;
            }

            if (amount <= 0) {
                return true;
            }

            if (
                this.form.source_key === "wallet" &&
                !this.form.bank_id
            ) {
                return true;
            }

            if (this.walletInsufficient) {
                return true;
            }

            if (applied <= 0) {
                return true;
            }

            if (applied > amount) {
                return true;
            }

            // Mientras existan pendientes, todo el pago debe distribuirse.
            if (
                applied < pending &&
                amount !== applied
            ) {
                return true;
            }

            // El excedente necesita una cuenta destino.
            if (
                applied >= pending &&
                amount > applied &&
                !this.form.excess_bank_id
            ) {
                return true;
            }

            return false;
        },

        excessAmount() {
            const amount = this.roundMoney(this.form.amount);
            const applied = this.roundMoney(this.totalApplyAmount);
            const pending = this.roundMoney(this.totalPendingAmount);

            if (
                applied >= pending &&
                amount > applied
            ) {
                return this.roundMoney(amount - applied);
            }

            return 0;
        }
    },

    methods: {
        getDefaultForm() {
            return {
                program_group_id: "",
                user_id: "",
                company_id: "",
                bank_id: "",
                amount: "",
                source_key: "",
                type_payments_id: null,
                bank_account_company_id: "",
                excess_bank_id: "",
                type_program: 1,
                date: this.formatDate(new Date())
            };
        },

        formatDate(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");

            return `${year}-${month}-${day}`;
        },

        roundMoney(value) {
            return Math.round(
                (Number(value || 0) + Number.EPSILON) * 100
            ) / 100;
        },

        clearError(field) {
            if (this.errors[field]) {
                this.$delete(this.errors, field);
            }
        },

        getAccountLabel(account) {
            if (!account || !account.account_info) {
                return "Cuenta bancaria";
            }

            const accountInfo = account.account_info;

            const name = accountInfo.name || "Cuenta bancaria";
            const number = accountInfo.number || `Cuenta #${accountInfo.id}`;
            const bankName =
                accountInfo.bank && accountInfo.bank.name
                    ? accountInfo.bank.name
                    : "Sin banco";

            return `${name} - ${number} / ${bankName}`;
        },

        getSlotProgress(slot) {
            const expected = Number(
                slot.expected_amount ||
                slot.amount ||
                0
            );

            const paid = Number(slot.paid_amount || 0);

            if (expected <= 0) {
                return 0;
            }

            return Math.min(
                Math.round((paid / expected) * 100),
                100
            );
        },

        closeModal() {
            this.errors = {};
            this.wallet_balance = 0;
            this.contributionSlots = [];
            this.form = this.getDefaultForm();
            this.contribution_count = 0;
            this.extra_contribution_count = 0;
            this.loadingSlots = false;
            this.saving = false;

            this.$modal.hide("modalProgramGroupContribution");
        },

        normalizeApplyAmount(slot) {
            let value = Number(slot.apply_amount || 0);

            const pending = Number(
                slot.pending_amount ||
                slot.amount ||
                slot.expected_amount ||
                0
            );

            if (value < 0) {
                value = 0;
            }

            if (value > pending) {
                value = pending;
            }

            slot.apply_amount = this.roundMoney(value);
        },

        applyFullPending(slot) {
            slot.apply_amount = this.roundMoney(
                slot.pending_amount ||
                slot.amount ||
                slot.expected_amount ||
                0
            );
        },

        distributeAutomatically() {
            let remaining = this.roundMoney(this.form.amount);

            this.contributionSlots.forEach(slot => {
                const pending = this.roundMoney(
                    slot.pending_amount || 0
                );

                const amountToApply = Math.min(
                    pending,
                    Math.max(remaining, 0)
                );

                slot.apply_amount = this.roundMoney(
                    amountToApply
                );

                remaining = this.roundMoney(
                    remaining - amountToApply
                );
            });
        },

        clearDistribution() {
            this.contributionSlots.forEach(slot => {
                slot.apply_amount = 0;
            });
        },

        buildInstallmentsPayload() {
            return this.contributionSlots
                .filter(slot => Number(slot.apply_amount) > 0)
                .map(slot => ({
                    program_installment_id: slot.id,
                    aportacion_number: slot.number,
                    amount: Number(slot.apply_amount)
                }));
        },

        async saveContribution() {
            if (this.isSaveDisabled || this.saving) {
                return;
            }

            const installments = this.buildInstallmentsPayload();

            const payload = {
                program_group_id: this.form.program_group_id,
                user_id: this.form.user_id,
                company_id: this.form.company_id,
                bank_id: this.form.bank_id,
                amount: Number(this.form.amount),
                source_key: this.form.source_key,
                type_payments_id: this.form.type_payments_id,
                bank_account_company_id:
                    this.form.bank_account_company_id,
                excess_amount: Number(this.excessAmount || 0),
                excess_bank_id: this.form.excess_bank_id,
                date: this.form.date,
                type_program: 1,
                installments
            };

            this.saving = true;
            this.errors = {};

            try {
                const { data } = await request.post(
                    "/program-group-contributions",
                    payload
                );

                this.successNotification(data.message);
                this.closeModal();

                Event.$emit(
                    "event-reload-program-relations"
                );
            } catch (error) {
                this.errors = await this.errorNotification(error);
            } finally {
                this.saving = false;
            }
        },

        async loadWallet() {
            this.clearError("bank_id");

            if (
                !this.form.user_id ||
                !this.form.bank_id ||
                !this.form.company_id
            ) {
                this.wallet_balance = 0;
                return;
            }

            const user_id = this.form.user_id;
            const company_id = this.form.company_id;
            const bank_id = this.form.bank_id;

            try {
                const { data } = await request.get(
                    `/wallet/${user_id}/${company_id}/${bank_id}`
                );

                this.wallet_balance = Number(
                    data.balance || 0
                );
            } catch (error) {
                console.log(error);
                this.wallet_balance = 0;
            }
        },

        async loadContributionSlots(program_id) {
            this.loadingSlots = true;

            try {
                const url =
                    `/programs/${program_id}/contribution-slots`;

                const { data } = await request.get(url, {
                    program_group_id:
                        this.form.program_group_id,
                    user_id: this.form.user_id,
                    company_id: this.form.company_id
                });

                this.contributionSlots = (
                    data.slots || []
                ).map(slot => ({
                    ...slot,
                    apply_amount: 0,
                    pending_amount: Number(
                        slot.pending_amount ||
                        slot.amount ||
                        slot.expected_amount ||
                        0
                    )
                }));
            } finally {
                this.loadingSlots = false;
            }
        },

        ...mapActions({
            errorNotification: "alert/error",
            successNotification: "alert/success"
        })
    },

    created() {
        Event.$on(
            "event-open-program-group-contribution",
            async payload => {
                this.form.program_group_id =
                    payload.program_group_id;

                this.form.user_id =
                    payload.user_id;

                this.form.company_id =
                    payload.company_id;

                this.$modal.show(
                    "modalProgramGroupContribution"
                );

                try {
                    await this.catalogue
                        .get_bank_accounts_groups(
                            payload.program_group_id
                        );

                    await this.loadContributionSlots(
                        payload.program_id
                    );

                    await this.catalogue
                        .getBankAccountsCompanies(
                            payload.companies_id
                        );
                } catch (error) {
                    await this.errorNotification(error);
                }
            }
        );
    }
};
</script>

<style scoped>
.contribution-form {
    --primary: #5672a5;
    --primary-dark: #304d7d;
    --primary-soft: #eef3fb;
    --success: #269b74;
    --success-soft: #eaf8f3;
    --warning: #d99b24;
    --warning-soft: #fff8e8;
    --danger: #d84d5d;
    --danger-soft: #fff0f2;
    --border: #dfe5ee;
    --surface: #ffffff;
    --background: #f5f7fb;
    --text: #1f2937;
    --muted: #6b7280;

    background: var(--background);
    color: var(--text);
    min-height: 400px;
}

.form-header {
    align-items: center;
    background: linear-gradient(135deg,
            var(--primary-dark),
            var(--primary));
    color: #ffffff;
    display: flex;
    justify-content: space-between;
    padding: 18px 22px;
}

.form-header__content {
    align-items: center;
    display: flex;
    gap: 14px;
}

.form-header__icon {
    align-items: center;
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    display: flex;
    font-size: 22px;
    height: 46px;
    justify-content: center;
    width: 46px;
}

.form-header__title {
    font-size: 18px;
    font-weight: 700;
    margin: 0;
}

.form-header__subtitle {
    color: rgba(255, 255, 255, 0.78);
    font-size: 12px;
    margin: 3px 0 0;
}

.form-close {
    align-items: center;
    background: rgba(255, 255, 255, 0.12);
    border: 0;
    border-radius: 50%;
    color: #ffffff;
    cursor: pointer;
    display: flex;
    height: 36px;
    justify-content: center;
    transition: 0.2s ease;
    width: 36px;
}

.form-close:hover {
    background: rgba(255, 255, 255, 0.24);
    transform: rotate(90deg);
}

.form-body {
    max-height: calc(100vh - 185px);
    overflow-y: auto;
    padding: 22px;
}

.contribution-layout {
    align-items: start;
    display: grid;
    gap: 20px;
    grid-template-columns: minmax(0, 1fr) 300px;
}

.contribution-main {
    min-width: 0;
}

.form-section {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    box-shadow: 0 3px 10px rgba(15, 23, 42, 0.04);
    margin-bottom: 18px;
    padding: 20px;
}

.form-section--distribution {
    padding-left: 0;
    padding-right: 0;
}

.form-section--distribution .section-header {
    padding-left: 20px;
    padding-right: 20px;
}

.section-header {
    align-items: flex-start;
    display: flex;
    gap: 12px;
    margin-bottom: 18px;
}

.section-header--actions {
    align-items: center;
    justify-content: space-between;
}

.section-header__left {
    align-items: flex-start;
    display: flex;
    gap: 12px;
}

.section-number {
    align-items: center;
    background: var(--primary-soft);
    border-radius: 50%;
    color: var(--primary-dark);
    display: flex;
    flex: 0 0 auto;
    font-size: 13px;
    font-weight: 700;
    height: 30px;
    justify-content: center;
    width: 30px;
}

.section-number--success {
    background: var(--success-soft);
    color: var(--success);
}

.section-title {
    color: var(--text);
    font-size: 15px;
    font-weight: 700;
    margin: 1px 0 3px;
}

.section-description {
    color: var(--muted);
    font-size: 12px;
    margin: 0;
}

.source-options {
    display: grid;
    gap: 12px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.source-option {
    align-items: center;
    background: #ffffff;
    border: 1px solid var(--border);
    border-radius: 10px;
    cursor: pointer;
    display: flex;
    gap: 12px;
    margin: 0;
    min-height: 84px;
    padding: 14px;
    position: relative;
    transition: 0.2s ease;
}

.source-option:hover {
    border-color: var(--primary);
    box-shadow: 0 4px 12px rgba(48, 77, 125, 0.08);
    transform: translateY(-1px);
}

.source-option--active {
    background: var(--primary-soft);
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(86, 114, 165, 0.08);
}

.source-option input {
    height: 1px;
    opacity: 0;
    position: absolute;
    width: 1px;
}

.source-option__icon {
    align-items: center;
    background: #f3f5f8;
    border-radius: 10px;
    color: var(--primary);
    display: flex;
    flex: 0 0 auto;
    font-size: 18px;
    height: 42px;
    justify-content: center;
    width: 42px;
}

.source-option--active .source-option__icon {
    background: #ffffff;
}

.source-option__content {
    display: flex;
    flex: 1;
    flex-direction: column;
}

.source-option__content strong {
    color: var(--text);
    font-size: 13px;
}

.source-option__content small {
    color: var(--muted);
    font-size: 11px;
    line-height: 1.35;
    margin-top: 3px;
}

.source-option__check {
    color: var(--primary);
    font-size: 17px;
    opacity: 0;
}

.source-option--active .source-option__check {
    opacity: 1;
}

.wallet-panel {
    background: #f8fafc;
    border: 1px solid var(--border);
    border-radius: 10px;
    display: grid;
    gap: 14px;
    grid-template-columns: minmax(0, 1fr) 210px;
    margin-top: 16px;
    padding: 16px;
}

.wallet-balance {
    align-items: center;
    background: var(--success-soft);
    border: 1px solid #c7ecde;
    border-radius: 10px;
    color: var(--success);
    display: flex;
    gap: 12px;
    padding: 12px 14px;
}

.wallet-balance--danger {
    background: var(--danger-soft);
    border-color: #f2c7cd;
    color: var(--danger);
}

.wallet-balance__icon {
    align-items: center;
    background: rgba(255, 255, 255, 0.65);
    border-radius: 50%;
    display: flex;
    height: 36px;
    justify-content: center;
    width: 36px;
}

.wallet-balance small,
.wallet-balance strong {
    display: block;
}

.wallet-balance small {
    font-size: 11px;
}

.wallet-balance strong {
    font-size: 15px;
}

.wallet-panel .validation-message {
    grid-column: 1 / -1;
}

.form-grid {
    display: grid;
    gap: 16px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.form-group {
    margin-bottom: 0;
}

.field-label {
    color: #374151;
    display: block;
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 6px;
}

.required-mark {
    color: var(--danger);
}

.field-help {
    color: var(--muted);
    display: block;
    font-size: 10px;
    margin-top: 4px;
}

.field-error {
    color: var(--danger);
    font-size: 11px;
    margin-top: 5px;
}

.form-control {
    border-color: #d5dce6;
    border-radius: 7px;
    font-size: 12px;
    min-height: 38px;
}

.form-control:focus {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(86, 114, 165, 0.12);
}

.date-picker-field {
    width: 100%;
}

.amount-field {
    display: flex;
    position: relative;
}

.amount-field__symbol {
    align-items: center;
    background: #f3f5f8;
    border: 1px solid #d5dce6;
    border-radius: 7px 0 0 7px;
    color: #4b5563;
    display: flex;
    font-weight: 700;
    justify-content: center;
    width: 38px;
}

.amount-field__input {
    border-left: 0;
    border-radius: 0 7px 7px 0;
    font-size: 15px;
    font-weight: 700;
}

.distribution-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.loading-state,
.empty-state {
    align-items: center;
    color: var(--muted);
    display: flex;
    gap: 12px;
    justify-content: center;
    min-height: 150px;
    padding: 30px;
    text-align: left;
}

.loading-state i {
    color: var(--primary);
    font-size: 22px;
}

.empty-state__icon {
    align-items: center;
    background: var(--success-soft);
    border-radius: 50%;
    color: var(--success);
    display: flex;
    flex: 0 0 auto;
    font-size: 22px;
    height: 48px;
    justify-content: center;
    width: 48px;
}

.empty-state strong {
    color: var(--text);
    display: block;
}

.empty-state p {
    font-size: 12px;
    margin: 3px 0 0;
}

.distribution-table-wrapper {
    border-bottom: 1px solid var(--border);
    border-top: 1px solid var(--border);
    max-height: 420px;
    overflow: auto;
}

.distribution-table {
    border-collapse: separate;
    border-spacing: 0;
    margin: 0;
    min-width: 850px;
    width: 100%;
}

.distribution-table th {
    background: #f3f5f8;
    border-bottom: 1px solid var(--border);
    color: #4b5563;
    font-size: 11px;
    font-weight: 700;
    padding: 11px 13px;
    position: sticky;
    text-transform: uppercase;
    top: 0;
    z-index: 2;
}

.distribution-table td {
    border-bottom: 1px solid #edf0f4;
    font-size: 12px;
    padding: 12px 13px;
    vertical-align: middle;
}

.distribution-table tbody tr:hover {
    background: #fafbfd;
}

.slot-information {
    align-items: center;
    display: flex;
    gap: 10px;
}

.slot-icon {
    align-items: center;
    background: var(--primary-soft);
    border-radius: 8px;
    color: var(--primary);
    display: flex;
    flex: 0 0 auto;
    height: 34px;
    justify-content: center;
    width: 34px;
}

.slot-information strong {
    display: block;
    font-size: 12px;
}

.slot-information small {
    color: var(--muted);
    display: block;
    font-size: 10px;
    margin-top: 2px;
}

.slot-progress {
    background: #edf0f4;
    border-radius: 999px;
    height: 4px;
    margin-top: 8px;
    overflow: hidden;
    width: 100%;
}

.slot-progress__bar {
    background: var(--success);
    border-radius: inherit;
    height: 100%;
    transition: width 0.2s ease;
}

.amount-cell {
    color: #4b5563;
}

.paid-cell {
    color: var(--success);
    font-weight: 600;
}

.pending-cell {
    color: var(--warning);
    font-weight: 700;
}

.apply-column {
    min-width: 185px;
    width: 185px;
}

.apply-field {
    align-items: center;
    display: flex;
    gap: 6px;
}

.apply-full-button {
    background: var(--primary-soft);
    border: 0;
    border-radius: 6px;
    color: var(--primary-dark);
    cursor: pointer;
    font-size: 10px;
    font-weight: 700;
    height: 31px;
    padding: 0 9px;
}

.apply-full-button:hover {
    background: var(--primary);
    color: #ffffff;
}

.distribution-validations {
    display: grid;
    gap: 8px;
    padding: 14px 20px 0;
}

.validation-message {
    align-items: flex-start;
    border-radius: 8px;
    display: flex;
    font-size: 11px;
    gap: 8px;
    line-height: 1.4;
    padding: 10px 12px;
}

.validation-message--danger {
    background: var(--danger-soft);
    color: var(--danger);
}

.validation-message--warning {
    background: var(--warning-soft);
    color: #9a6814;
}

.excess-section {
    background: #f7fcfa;
    border-color: #c8eadf;
}

.excess-summary {
    align-items: center;
    background: var(--success-soft);
    border-radius: 8px;
    color: var(--success);
    display: flex;
    justify-content: space-between;
    margin-bottom: 15px;
    padding: 12px 14px;
}

.excess-summary span {
    font-size: 12px;
}

.excess-summary strong {
    font-size: 16px;
}

.contribution-summary {
    position: sticky;
    top: 0;
}

.summary-card {
    background: #ffffff;
    border: 1px solid var(--border);
    border-radius: 12px;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
    overflow: hidden;
}

.summary-card__header {
    align-items: center;
    background: var(--primary-dark);
    color: #ffffff;
    display: flex;
    justify-content: space-between;
    padding: 16px;
}

.summary-card__header small,
.summary-card__header strong {
    display: block;
}

.summary-card__header small {
    color: rgba(255, 255, 255, 0.72);
    font-size: 10px;
}

.summary-card__header strong {
    font-size: 15px;
}

.summary-card__header i {
    font-size: 22px;
    opacity: 0.7;
}

.summary-row {
    align-items: center;
    border-bottom: 1px solid #edf0f4;
    display: flex;
    font-size: 11px;
    justify-content: space-between;
    padding: 12px 15px;
}

.summary-row span {
    color: var(--muted);
}

.summary-row strong {
    color: var(--text);
    font-size: 12px;
}

.summary-row--primary {
    background: var(--primary-soft);
}

.summary-row--primary strong {
    color: var(--primary-dark);
    font-size: 14px;
}

.summary-row--success {
    background: var(--success-soft);
}

.summary-row--success strong {
    color: var(--success);
}

.summary-row--warning {
    background: var(--warning-soft);
}

.summary-row--warning strong {
    color: #9a6814;
}

.summary-progress {
    padding: 15px;
}

.summary-progress__header {
    align-items: center;
    display: flex;
    font-size: 10px;
    justify-content: space-between;
    margin-bottom: 7px;
}

.summary-progress__header span {
    color: var(--muted);
}

.summary-progress__track {
    background: #edf0f4;
    border-radius: 999px;
    height: 8px;
    overflow: hidden;
}

.summary-progress__value {
    background: var(--success);
    border-radius: inherit;
    height: 100%;
    transition: width 0.25s ease;
}

.summary-wallet {
    align-items: center;
    background: var(--success-soft);
    color: var(--success);
    display: flex;
    font-size: 10px;
    justify-content: space-between;
    padding: 12px 15px;
}

.summary-wallet--danger {
    background: var(--danger-soft);
    color: var(--danger);
}

.summary-help {
    align-items: flex-start;
    background: #eef5ff;
    border: 1px solid #d6e6fb;
    border-radius: 10px;
    color: #42658f;
    display: flex;
    font-size: 10px;
    gap: 8px;
    margin-top: 12px;
    padding: 11px;
}

.summary-help p {
    line-height: 1.45;
    margin: 0;
}

.form-footer {
    align-items: center;
    background: #ffffff;
    border-top: 1px solid var(--border);
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    padding: 14px 22px;
}

.footer-button {
    border-radius: 7px;
    font-size: 12px;
    min-width: 120px;
    padding: 9px 15px;
}

.footer-button--save {
    min-width: 175px;
}

.text-right {
    text-align: right;
}

.is-invalid {
    border-color: var(--danger) !important;
}

@media (max-width: 1100px) {
    .contribution-layout {
        grid-template-columns: 1fr;
    }

    .contribution-summary {
        position: static;
    }
}

@media (max-width: 768px) {
    .form-body {
        padding: 14px;
    }

    .form-grid,
    .source-options,
    .wallet-panel {
        grid-template-columns: 1fr;
    }

    .section-header--actions {
        align-items: flex-start;
        flex-direction: column;
    }

    .distribution-actions {
        width: 100%;
    }

    .distribution-actions .btn {
        flex: 1;
    }

    .form-footer {
        flex-direction: column-reverse;
    }

    .footer-button {
        width: 100%;
    }
}
</style>